<template>
  <div v-if="!loading">
    <div class="work-order-info">
      <v-row>
        <v-col
          cols="12"
          md="10"
          lg="10"
          xl="10"
          xs="12"
          sm="12"
          class="d-flex align-center justify-center"
        >
          <v-row>
            <v-col
              cols="12"
              md="4"
              lg="4"
              xl="4"
              xs="12"
              sm="12"
            >
              <VTextField
                v-model="model.templateName"
                :label="$t('forms.labels.workOrderTemplate.name')"
                dense
                prependIcon="edit"
                @input="updateDirty"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
              lg="4"
              xl="4"
              xs="12"
              sm="12"
            >
              <v-select
                v-model="model.workOrderType"
                :items="getWOTypes"
                :label="$t('forms.labels.workOrderTemplate.workOrderType')"
                clearable
                dense
                itemValue="id"
                :itemText="'name'"
                @input="updateDirty"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
              lg="4"
              xl="4"
              xs="12"
              sm="12"
            >
              <WorkOrderTemplateType
                :templateType="model.templateType"
                :isSite="!!site.id"
                @sendData="templateTypeChanged"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col
          cols="12"
          md="2"
          lg="2"
          xl="2"
          class="d-flex align-center justify-center"
        >
          <v-btn
            class="save-btn"
            :color="'#EF4135'"
            :disabled="isSaveDisabled()"
            @click="save"
          >
            {{ $t('forms.btn.save') }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div
      class="summary-section-wrap"
    >
      <Section
        class="mb-1"
        :isEditable="isEditable"
        :showEye="false"
        :isWorkOrderTemplate="true"
        :title="$t('workOrdersTemplate.labels.summary')"
        :visible="model.summaryReport.showGeneral"
        :showChildren="showChildren"
      >
        <MainInfo
          :hasNotType="hasNotType"
          :isCustom="true"
          :model="model.summaryInfo"
          :disableClientAndSiteInfo="isTeam || isPersonal"
          @hasNotType="hasNotType=false"
        />
      </Section>

      <Section
        v-if="hasPermission('comments.view')"
        class="mb-1"
        :isEditable="isEditable"
        :title="$t('forms.labels.openingComment')"
        :visible="model.summaryReport.showOpeningComments"
        :showChildren="showChildren"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showOpeningComments = $event"
      >
        <GlobalComment
          position="opening"
          :canUploadImages="false"
          :isWOTemplate="true"
          :isLab="false"
          :comment="{ message: model.openingComment}"
          @message="updateComments"
        />
      </Section>

      <Section
        class="mb-1"
        :isEditable="isEditable"
        :showArrowIcon="!!networks.length"
        :title="$t('workOrdersTemplate.labels.analyses')"
        :visible="model.summaryReport.showAnalyses"
        :showChildren="showChildren"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showAnalyses = $event"
      >
        <template v-for="(network, index) in networks">
          <Section
            :key="index"
            class="mb-1"
            :isEditable="isEditable"
            :showArrowIcon="false"
            :title="network.name"
            :showEye="true"
            :hasChildren="true"
            :visible="network.showNetwork"
            :showChildren="false"
            :isWorkOrderTemplate="true"
            @state-changed="network.showNetwork = $event"
          />
        </template>
      </Section>
      <Section
        class="mb-1"
        :isEditable="isEditable"
        :title="$t('workOrdersTemplate.labels.flowMeters')"
        :showArrowIcon="false"
        :showChildren="showChildren"
        :visible="model.summaryReport.showFlowMeter"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showFlowMeter = $event"
      />
      <Section
        class="mb-1"
        :isEditable="isEditable"
        :title="$t('workOrdersTemplate.labels.productLevel')"
        :showArrowIcon="false"
        :showChildren="showChildren"
        :visible="model.summaryReport.showProductLevel"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showProductLevel = $event"
      />
      <Section
        class="mb-1"
        :isEditable="false"
        :title="$t('workOrdersTemplate.labels.massBalance')"
        :showArrowIcon="false"
        :showChildren="showChildren"
        :visible="model.summaryReport.showMassBalance"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showMassBalance = $event"
      />
      <Section
        class="mb-1"
        :isEditable="isEditable"
        :title="$t('workOrdersTemplate.labels.analysisRequests')"
        :showArrowIcon="false"
        :visible="model.summaryReport.showSamples"
        :showChildren="showChildren"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showSamples = $event"
      />
      <Section
        class="mb-1"
        :isEditable="isEditable"
        :title="$t('workOrdersTemplate.labels.inventory')"
        :showArrowIcon="false"
        :visible="model.summaryReport.showInventory"
        :showChildren="showChildren"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showInventory = $event"
      />

      <Section
        v-if="hasPermission('comments.view')"
        class="mb-1"
        :isEditable="isEditable"
        :showArrowIcon="true"
        :title="$t('workOrdersTemplate.labels.commentsAndPictures')"
        :visible="model.summaryReport.showAnnexComments"
        :showChildren="showChildren"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showAnnexComments = $event"
      >
        <GlobalComment
          position="comments"
          :isLab="false"
          :isWOTemplate="true"
          :canUploadImages="false"
          :comment="{ message: model.commentComment}"
          @message="updateComments"
        />
      </Section>

      <Section
        v-if="hasPermission('comments.view')"
        class="mb-1"
        :isEditable="isEditable"
        :title="$t('forms.labels.closingComment')"
        :visible="model.summaryReport.showClosingComments"
        :showChildren="showChildren"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showClosingComments = $event"
      >
        <GlobalComment
          position="closing"
          :canUploadImages="false"
          :isLab="false"
          :isWOTemplate="true"
          :comment="{ message: model.closingComment}"
          @message="updateComments"
        />
      </Section>
      <Section
        class="mb-1"
        :title="$t('forms.labels.appendices')"
        :isEditable="isEditable"
        :visible="model.summaryReport.showAppendices"
        :showChildren="showChildren"
        :showArrowIcon="false"
        :isWorkOrderTemplate="true"
        @state-changed="model.summaryReport.showAppendices = $event"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import completionStatus from '@/constants/workOrderStatusChoices';
import GlobalComment from '@/views/laboratory/workOrder/summary/GlobalComment';
import MainInfo from '@/views/workOrders/shared/MainInfo.vue';
import Section from '@/views/workOrders/shared/Section.vue';
import WorkOrderTemplateType from '@/components/forms/workOrder/WorkOrderTemplateType';
import templateType from '@/constants/workOrderTemplateTypes';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';


export default {
    components: {
        GlobalComment,
        MainInfo,
        Section,
        WorkOrderTemplateType,
        VSelect,
    },
    data() {
        return {
            loading: true,
            isEditable: false,
            hasNotType: false,
            isReopened: false,
            showChildren: false,
            comments: [],
            model: {
                templateName: null,
                workOrderType: null,
                openingComment: '',
                closingComment: '',
                commentComment: '',
                templateType: null,
                summaryInfo: {
                    name: '',
                    type: '',
                    po: '',
                    so: '',
                    billable: false,
                    site: {
                        client: {
                            name: '',
                            id: null,
                        },
                        id: null,
                        name: '',
                        address: '',
                        isEditable: true,
                    },
                    reportName: '',
                },
                summaryReport: {
                    showGeneral: true,
                    showOpeningComments: true,
                    showAnalyses: true,
                    showSamples: true,
                    showMassBalance: true,
                    showInventory: true,
                    showAnnexComments: true,
                    showClosingComments: true,
                    showAppendices: true,
                    showFlowMeter: true,
                    showProductLevel: true,
                },
            },
            teams: [templateType.PERSONAL],
            workOrderTypes: [],
            networks: [],
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'hasRole',
            'lang',
            'client',
            'site',
            'woTemplate',
            'teamMainLang',
            'userLang',
        ]),
        isTypeTeam() {
            if (this.isAdminOrManager()) return false;
            return this.teams[this.model.templateType] === templateType.TEAM;
        },
        getWOTypes() {
            return this.workOrderTypes.map(wType => ({ ...wType, name: wType.name[this.userLang] || wType.name[this.teamMainLang] }));
        },
        isTeam() {
            return this.model.templateType === this.teams.indexOf(templateType.TEAM);
        },
        isPersonal() {
            return this.model.templateType === this.teams.indexOf(templateType.PERSONAL);
        },
    },

    async mounted() {
        this.loading = true;
        this.initTemplateType();
        if (this.$route.name === 'siteCreateTemplate') {
            this.initSiteAndClientInfo();
        }
        if (this.$route.name === 'siteEditTemplate') {
            this.getNetworkSite();
        }
        await this.getWorkOrderTypes();
        if (this.woTemplate.id) {
            this.initTemplate();
        }
        this.loading = false;
    },

    methods: {
        ...mapActions([
            'resetWOTemplateInfo',
        ]),

        ...mapActions({
            disablePreventQuitFormular: 'disablePreventQuitFormular',
            activePreventQuitFormular: 'activePreventQuitFormular',
        }),
        isAdminOrManager() {
            return this.hasRole('manager') || this.hasRole('administrator');
        },
        async getWorkOrderTypes() {
            const { data } = await this.$rq.getWorkOrderTypes();
            this.workOrderTypes = data;
        },
        initTemplateType() {
            if ((this.hasRole('manager') || this.hasRole('administrator'))) {
                this.teams.push(templateType.TEAM);
            }
            if (this.site.id) {
                this.teams.push(templateType.SITE);
            }
        },


        initSiteAndClientInfo() {
            this.model.summaryInfo.site = {
                client: {
                    name: this.client.name,
                    id: this.client.id,
                },
                id: this.site.id,
                name: this.site.name,
                address: this.site.address,
                isEditable: true,
                reportName: this.woTemplate.reportName,
            };
            this.getNetworkSite();
        },
        async getNetworkSite() {
            const { data } = await this.$rq.getNetworkBySite(this.site.id);
            this.networks = data.networks.map(network => {
                /* eslint no-param-reassign: "error" */
                if (this.woTemplate.id && !this.woTemplate.networks.length) {
                    network.showNetwork = false;
                    return network;
                }
                const canShow = this.woTemplate.id ? this.woTemplate.networks.includes(network.id) : true;
                network.showNetwork = canShow || false;
                return network;
            });
        },
        templateTypeChanged({ type }) {
            const newType = this.teams[type];
            this.model.templateType = type;
            const isSiteorTemplate = (newType === templateType.SITE || newType === templateType.PERSONAL);
            this.model.summaryInfo.site.isEditable = !isSiteorTemplate;
            this.model.summaryInfo.site = this.getSiteInfo();
            this.activePreventQuitFormular();
        },
        initTemplate() {
            this.model = {
                templateName: this.woTemplate.title,
                workOrderType: this.woTemplate.workOrderType?.id,
                openingComment: this.woTemplate.openingComment,
                closingComment: this.woTemplate.closingComment,
                commentComment: this.woTemplate.commentComment,
                templateType: this.teams.indexOf(this.woTemplate.templateType),
                summaryInfo: {
                    name: this.woTemplate.report?.name,
                    type: this.woTemplate.report?.typeId,
                    po: this.woTemplate.poNumber,
                    so: this.woTemplate.soNumber,
                    billable: this.woTemplate.billable,
                    site: this.getSiteInfo(),
                    reportName: this.woTemplate.reportName,
                    reportType: this.woTemplate.reportType,
                },
                summaryReport: { ...this.woTemplate.summaryReport },
            };
        },
        getSiteInfo() {
            const emptyObj = {
                client: {
                    name: '',
                    id: '',
                },
                id: '',
                name: '',
                address: '',
                isEditable: this.isTeam,
            };
            return this.isTeam || this.isPersonal ? emptyObj : {
                client: {
                    name: this.woTemplate.id ? this.woTemplate.client?.name : this.client?.name,
                    id: this.woTemplate.id ? this.woTemplate.client?.id : this.client?.id,
                },
                id: this.woTemplate.id ? this.woTemplate.site?.id : this.site?.id,
                name: this.woTemplate.id ? this.woTemplate.site?.name : this.site?.name,
                address: this.woTemplate.id ? this.woTemplate.site?.address : this.site?.address,
                isEditable: this.isTeam,
            };
        },
        isSaveDisabled() {
            return !(this.model.templateType !== null && this.model.templateName && this.model.templateName.replace(/\s/g, '') !== ''
              && this.model.workOrderType);
        },
        async save() {
            this.disablePreventQuitFormular();

            const requestObject = {
                title: this.model.templateName,
                templateType: this.teams[this.model.templateType],
                site: this.model.summaryInfo.site?.id?.length ? this.model.summaryInfo.site.id : this.site?.id || null,
                client: this.model.summaryInfo.site.client?.id?.length ? this.model.summaryInfo.site.client.id : this.client?.id || null,
                workOrderType: this.model.workOrderType,
                reportType: this.model.summaryInfo?.type?.length ? this.model?.summaryInfo?.type : null,
                openingComment: this.model?.openingComment,
                closingComment: this.model?.closingComment,
                commentComment: this.model?.commentComment,
                poNumber: this.model?.summaryInfo?.po,
                soNumber: this.model?.summaryInfo?.so,
                reportName: this.model?.summaryInfo?.name,
                billable: this.model?.summaryInfo?.billable,
                networks: this.getSelectedNetwork(),
                summaryReport: { ...this.model?.summaryReport },
            };
            const { data } = this.woTemplate.id ? await this.$rq.editWOTemplate(this.woTemplate.id, requestObject)
                : await this.$rq.addWOTemplate(requestObject);
            if (data.id) {
                this.reload();
            }
        },
        async checkIsReopened(val) {
            this.isReopened = val;
            if (this.isReopened) {
                const { id } = this.workOrder;
                const payload = {};
                payload.completionStatus = completionStatus.IN_PROGRESS;
                await this.$rq.reopenWorkOrder(id);
                await this.$rq.updateWorkOrder(id, payload);
                await this.reloadWorkOrder(id);
                this.isReopened = false;
            }
        },
        getSelectedNetwork() {
            return this.model.templateType > 1 ? [] : this.networks.filter(network => network.showNetwork).map(network => network.id);
        },
        checkIsCompleted(val) {
            this.verifyIsCompleted = val;
        },
        updateComments(payload) {
            switch (payload.position) {
                case 'opening':
                    this.model.openingComment = payload.message;
                    break;
                case 'closing':
                    this.model.closingComment = payload.message;
                    break;
                case 'comments':
                    this.model.commentComment = payload.message;
                    break;
                default:
                    break;
            }
        },
        cleanData() {
            this.model = {
                templateName: null,
                workOrderType: null,
                openingComment: '',
                closingComment: '',
                commentComment: '',
                templateType: null,
                summaryInfo: {
                    name: '',
                    type: '',
                    po: '',
                    so: '',
                    billable: false,
                    site: {
                        client: {
                            name: '',
                            id: null,
                        },
                        id: null,
                        name: '',
                        address: '',
                    },
                },
                summaryReport: {
                    showOpeningComments: true,
                    showAnalyses: true,
                    showSamples: true,
                    showMassBalance: true,
                    showInventory: true,
                    showAnnexComments: true,
                    showClosingComments: true,
                    showAppendices: true,
                    showFlowMeter: true,
                    showProductLevel: true,
                },
            };
            this.resetWOTemplateInfo();
        },
        reload() {
            this.cleanData();

            let location = null;
            switch (true) {
                case window.location.pathname.includes('services'):
                    location = 'workOrdersTemplate';
                    break;
                case window.location.pathname.includes('sites'):
                    location = 'siteWorkOrdersTemplate';
                    break;
                default:
                    location = 'sites';
                    break;
            }

            this.$router.push({
                name: location,
                query: { exp: Date.now() },
            });
        },
        updateDirty() {
            this.activePreventQuitFormular();
        },
    },
};
</script>

<style lang="scss" scoped>
  .save-btn {
    color: white;
    float: right;
    background: 'secondary';
    border-radius: 1px;
  }
  .work-order-info {
    padding: 30px;
  }
</style>
